import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, MessageSquare, Lightbulb, Bug, HelpCircle, Send, Mail, Check } from 'lucide-react';

interface ContactUsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
}

type ContactType = 'idea' | 'bug' | 'other';

const ContactUsModal: React.FC<ContactUsModalProps> = ({ isOpen, onClose, userEmail }) => {
  const [selectedType, setSelectedType] = useState<ContactType | null>(null);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const contactOptions = [
    {
      type: 'idea' as ContactType,
      icon: Lightbulb,
      title: 'Share an Idea',
      description: 'Suggest a feature or improvement',
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'from-blue-50 to-cyan-50',
      borderColor: 'border-blue-200',
      hoverBorder: 'hover:border-blue-400'
    },
    {
      type: 'bug' as ContactType,
      icon: Bug,
      title: 'Report a Bug',
      description: 'Let us know about an issue',
      color: 'from-red-500 to-rose-600',
      bgColor: 'from-red-50 to-rose-50',
      borderColor: 'border-red-200',
      hoverBorder: 'hover:border-red-400'
    },
    {
      type: 'other' as ContactType,
      icon: HelpCircle,
      title: 'Other',
      description: 'General inquiry or feedback',
      color: 'from-purple-500 to-indigo-600',
      bgColor: 'from-purple-50 to-indigo-50',
      borderColor: 'border-purple-200',
      hoverBorder: 'hover:border-purple-400'
    }
  ];

  const handleSend = async () => {
    if (!selectedType || !message.trim()) {
      return;
    }

    setIsSending(true);

    try {
      // Send email via Vercel noorai-api backend
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://noorai-api.vercel.app';
      const response = await fetch(`${apiBaseUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          from: userEmail,
          type: selectedType,
          message: message.trim(),
          subject: `[${selectedType.toUpperCase()}] Contact Form Submission`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      // Show success message
      setShowSuccess(true);
      
      // Reset form after a delay
      setTimeout(() => {
        setShowSuccess(false);
        setSelectedType(null);
        setMessage('');
        setIsSending(false);
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again or email us directly at support@noorcad.com');
      setIsSending(false);
    }
  };

  const handleClose = () => {
    if (!isSending) {
      setSelectedType(null);
      setMessage('');
      setShowSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/60 to-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-slate-200/50">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">
                  Contact Us
                </h3>
                <p className="text-blue-100 text-sm">
                  We'd love to hear from you
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isSending}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-white/5 to-indigo-600/0 pointer-events-none"></div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6 max-h-[calc(90vh-180px)] overflow-y-auto">
          {showSuccess ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="p-4 bg-green-100 rounded-full">
                <Check className="w-12 h-12 text-green-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900">Message Sent!</h4>
              <p className="text-sm text-gray-600 text-center">
                Thank you for contacting us. We'll get back to you soon.
              </p>
            </div>
          ) : (
            <>
              {/* Email Field (Read-only) */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700 mb-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span>Your Email</span>
                </label>
                <input
                  type="email"
                  value={userEmail}
                  disabled
                  className="w-full px-4 py-3 bg-gray-100 border-2 border-gray-200 rounded-xl text-gray-600 cursor-not-allowed"
                />
              </div>

              {/* Contact Type Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  How can we help? <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {contactOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = selectedType === option.type;
                    
                    return (
                      <button
                        key={option.type}
                        onClick={() => setSelectedType(option.type)}
                        disabled={isSending}
                        className={`relative p-3 rounded-lg border-2 transition-all duration-200 ${
                          isSelected
                            ? `bg-gradient-to-br ${option.bgColor} ${option.borderColor} ring-2 ring-offset-1 ${option.borderColor.replace('border-', 'ring-')}`
                            : `bg-white border-gray-200 ${option.hoverBorder}`
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <div className={`p-1.5 rounded-md mb-2 bg-gradient-to-r ${option.color} w-fit mx-auto`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <h4 className="text-xs font-bold text-gray-900 mb-0.5 text-center">{option.title}</h4>
                        <p className="text-[10px] text-gray-600 leading-tight text-center">{option.description}</p>
                        {isSelected && (
                          <div className="absolute top-1.5 right-1.5">
                            <div className={`p-0.5 rounded-full bg-gradient-to-r ${option.color}`}>
                              <Check className="w-2.5 h-2.5 text-white" />
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Message Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={isSending}
                  placeholder="Tell us more about your feedback, idea, or issue..."
                  rows={6}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {message.length}/1000 characters
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-800">
                  💡 <strong>Tip:</strong> The more details you provide, the better we can help you!
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!showSuccess && (
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
            <div className="flex justify-end items-center space-x-3">
              <button
                onClick={handleClose}
                disabled={isSending}
                className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={!selectedType || !message.trim() || isSending}
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-lg shadow-blue-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Message
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ContactUsModal;
